import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, CalendarOff, Loader2, Check } from "lucide-react";
import { queueItem } from "@/lib/offlineQueue";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import OfflineBadge from "@/components/OfflineBadge";

const TYPES = { conge: "Congé", maladie: "Maladie", accident_travail: "Accident du travail", rtt: "RTT", autre: "Autre" };

const EMPTY = { salarie_id: "", type: "conge", date_debut: "", date_fin: "", motif: "" };

export default function Absences() {
  const [items, setItems] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([api.get("/absences"), api.get("/salaries")]);
    setItems(a.data); setSalaries(b.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useOfflineSync({ onSynced: () => load() });

  const submit = async (e) => {
    e.preventDefault();
    if (!navigator.onLine) {
      try { await queueItem("pending_absences", form); toast.success("Hors-ligne : absence enregistrée localement."); setOpen(false); setForm(EMPTY); }
      catch { toast.error("Enregistrement local impossible"); }
      return;
    }
    try { await api.post("/absences", form); toast.success("Absence enregistrée"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) {
      if (!err.response) {
        try { await queueItem("pending_absences", form); toast.success("Réseau instable : absence enregistrée localement."); setOpen(false); setForm(EMPTY); return; } catch {}
      }
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const approve = async (id) => {
    try { await api.post(`/absences/${id}/approve`); toast.success("Approuvée"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : "—"; };

  return (
    <div data-testid="absences-page">
      <PageHeader
        title="Absences, congés & arrêts"
        subtitle="Suivi des absences, congés payés, maladie et accidents du travail."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <OfflineBadge />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="abs-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle absence
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Déclarer une absence</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Salarié *</Label>
                  <Select value={form.salarie_id} onValueChange={(v)=>setForm({...form, salarie_id: v})}>
                    <SelectTrigger data-testid="abs-salarie"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{salaries.map(s=><SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Type *</Label>
                  <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
                    <SelectTrigger data-testid="abs-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TYPES).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Date début *</Label><Input type="date" required value={form.date_debut} onChange={(e)=>setForm({...form, date_debut: e.target.value})} /></div>
                <div className="space-y-2"><Label>Date fin *</Label><Input type="date" required value={form.date_fin} onChange={(e)=>setForm({...form, date_fin: e.target.value})} /></div>
                <div className="space-y-2 col-span-2"><Label>Motif</Label><Textarea rows={2} value={form.motif} onChange={(e)=>setForm({...form, motif: e.target.value})} /></div>
                <DialogFooter className="col-span-2"><Button type="submit" data-testid="abs-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <CalendarOff className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucune absence déclarée.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Salarié</th><th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Période</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{salLabel(a.salarie_id)}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{TYPES[a.type] || a.type}</span></td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{a.date_debut} → {a.date_fin}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${a.statut === "approuvee" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.statut}</span></td>
                  <td className="px-4 py-3 text-right">
                    {a.statut !== "approuvee" && <Button size="sm" variant="outline" onClick={()=>approve(a.id)} data-testid={`abs-approve-${a.id}`}><Check className="h-3 w-3 mr-1" />Approuver</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
